import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, Clock, MapPin, Download, Mail, Phone, CheckCircle2, Trophy, Utensils, Users, Euro, Info, Zap } from "lucide-react";

function fmtDateRange(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const sameMonth = s.getMonth() === e.getMonth();
  const fmtDay = (d) => d.getDate();
  const fmtMonth = (d) => d.toLocaleDateString("fr-FR", { month: "long" });
  const year = e.getFullYear();
  if (sameMonth) {
    return `du ${fmtDay(s)} au ${fmtDay(e)} ${fmtMonth(e)} ${year}`;
  }
  return `du ${fmtDay(s)} ${fmtMonth(s)} au ${fmtDay(e)} ${fmtMonth(e)} ${year}`;
}

export default function Stages() {
  const [stages, setStages] = useState([]);

  useEffect(() => {
    api.get("/stages").then((r) => setStages(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="stages-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute -top-20 -right-20 h-80 w-80 bg-[#D97706]/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-80 w-80 bg-[#DC2626]/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Vacances scolaires</span>
              <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]" style={{ fontFamily: "Outfit" }}>
                Stages de <span className="text-[#D97706]">foot</span>.
              </h1>
              <p className="mt-5 text-lg text-slate-200 max-w-xl leading-relaxed">
                Une semaine de football intensive pendant chaque vacances scolaires, pour perfectionner les gestes techniques et surtout… s'amuser entre copains.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-lg border border-white/15 bg-white/5 backdrop-blur p-6 space-y-3 text-sm" data-testid="stages-key-info">
                <div className="flex items-start gap-2.5">
                  <Calendar size={16} className="text-[#D97706] shrink-0 mt-0.5" />
                  <div><span className="font-bold">1 semaine</span> à chaque vacances scolaires (zone A)</div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Users size={16} className="text-[#D97706] shrink-0 mt-0.5" />
                  <div><span className="font-bold">U6 à U13</span> — garçons et filles</div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Euro size={16} className="text-[#D97706] shrink-0 mt-0.5" />
                  <div><span className="font-bold">100 €</span> la semaine</div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Utensils size={16} className="text-[#D97706] shrink-0 mt-0.5" />
                  <div><span className="font-bold">Pique-nique</span> à apporter chaque jour</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STAGES À VENIR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">À noter dans l'agenda</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
            Prochains stages
          </h2>
        </div>

        {stages.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-white p-10 text-center" data-testid="stages-empty">
            <Trophy className="h-10 w-10 text-[#CBD5E1] mx-auto" />
            <p className="mt-4 font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Prochain stage à venir — infos bientôt disponibles</p>
            <p className="mt-2 text-sm text-[#475569] max-w-md mx-auto">
              Les dates et horaires du prochain stage seront publiés ici dès qu'ils seront finalisés par le staff.
              Vous pouvez d'ores et déjà contacter le club pour pré-réserver une place.
            </p>
            <a href="mailto:presidencevufc@yahoo.com" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors" data-testid="stages-contact-cta">
              <Mail size={16} /> Nous écrire
            </a>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="stages-list">
            {stages.map((s, i) => (
              <div key={s.id} className={`relative rounded-lg border-2 ${i === 0 ? "border-[#D97706]" : "border-[#E2E8F0]"} bg-white p-6 lg:p-8`} data-testid={`stage-${s.id}`}>
                {i === 0 && (
                  <span className="absolute -top-3 left-6 px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#D97706] text-white rounded">Prochain stage</span>
                )}
                <div className="text-xs tracking-[0.2em] uppercase text-[#1E3A8A] font-bold">{s.name}</div>
                <h3 className="mt-2 text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
                  {fmtDateRange(s.start_date, s.end_date)}
                </h3>
                <div className="mt-5 space-y-2.5 text-sm">
                  <div className="flex items-start gap-2.5">
                    <Clock size={15} className="text-[#D97706] shrink-0 mt-0.5" />
                    <div><span className="font-semibold">Horaires :</span> {s.time_slot}</div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Users size={15} className="text-[#D97706] shrink-0 mt-0.5" />
                    <div><span className="font-semibold">Catégories :</span> {s.categories}</div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <MapPin size={15} className="text-[#D97706] shrink-0 mt-0.5" />
                    <div><span className="font-semibold">Lieu :</span> {s.location}</div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Utensils size={15} className="text-[#D97706] shrink-0 mt-0.5" />
                    <div><span className="font-semibold">Repas :</span> {s.lunch}</div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Euro size={15} className="text-[#D97706] shrink-0 mt-0.5" />
                    <div><span className="font-semibold">Tarif :</span> {s.price_eur} €/semaine</div>
                  </div>
                </div>
                {s.description && (
                  <p className="mt-5 text-sm text-[#475569] border-t border-[#E2E8F0] pt-4">{s.description}</p>
                )}
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.document_url ? (
                    <a href={s.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors" data-testid={`stage-doc-${s.id}`}>
                      <Download size={14} /> Dossier d'inscription
                    </a>
                  ) : (
                    <a href="mailto:presidencevufc@yahoo.com" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors">
                      <Mail size={14} /> Demander le dossier
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* COMMENT ÇA SE PASSE */}
      <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="mb-10">
            <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Déroulé type d'une journée</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
              Au programme
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Zap, title: "Technique", desc: "Travail des gestes : contrôle, passe, tir, dribble." },
              { icon: Trophy, title: "Matchs & tournois", desc: "Opposition quotidienne pour appliquer les apprentissages dans le jeu." },
              { icon: Users, title: "Jeux & cohésion", desc: "Jeux ludiques et moments d'équipe pour tisser des liens." },
              { icon: CheckCircle2, title: "Encadrement", desc: "Les éducateurs diplômés du club assurent toutes les séances." },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="rounded-lg border border-[#E2E8F0] bg-white p-5" data-testid={`stage-feat-${p.title}`}>
                  <Icon className="h-6 w-6 text-[#D97706] mb-4" />
                  <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{p.title}</div>
                  <p className="mt-2 text-sm text-[#475569]">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INSCRIPTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Comment s'inscrire</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
            Réservez la place de votre enfant
          </h2>
        </div>

        <div className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden grid lg:grid-cols-2">
          <div className="p-6 lg:p-10">
            <ol className="space-y-5">
              {[
                { n: 1, title: "Télécharger le dossier", desc: "Récupérez le dossier d'inscription du stage ci-dessus (ou demandez-le par email)." },
                { n: 2, title: "Compléter & régler", desc: "Remplissez les infos et joignez le règlement (chèque / espèces)." },
                { n: 3, title: "Déposer au secrétariat", desc: "Au Stade de la Viabert, avant la date limite indiquée sur le dossier." },
                { n: 4, title: "Confirmation", desc: "Le staff vous confirme la place par email ou téléphone." },
              ].map((s) => (
                <li key={s.n} className="flex gap-4">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-[#1E3A8A] text-white font-bold text-sm flex items-center justify-center" style={{ fontFamily: "Outfit" }}>
                    {s.n}
                  </div>
                  <div>
                    <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{s.title}</div>
                    <p className="text-sm text-[#475569] mt-0.5">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="bg-[#1E3A8A] text-white p-6 lg:p-10">
            <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Besoin d'infos ?</span>
            <h3 className="mt-3 text-xl font-bold" style={{ fontFamily: "Outfit" }}>Contactez le président</h3>
            <p className="mt-2 text-sm text-slate-200">Pour toute question concernant les stages, les horaires définitifs ou l'inscription, écrivez-nous directement.</p>
            <div className="mt-6 space-y-3">
              <a href="mailto:presidencevufc@yahoo.com" className="flex items-center gap-3 p-3 rounded-md bg-white/10 hover:bg-white/20 transition-colors" data-testid="stage-contact-email">
                <Mail size={16} className="shrink-0" />
                <div>
                  <div className="text-xs text-slate-300">Email</div>
                  <div className="text-sm font-semibold break-all">presidencevufc@yahoo.com</div>
                </div>
              </a>
              <a href="tel:0632475174" className="flex items-center gap-3 p-3 rounded-md bg-white/10 hover:bg-white/20 transition-colors" data-testid="stage-contact-phone">
                <Phone size={16} className="shrink-0" />
                <div>
                  <div className="text-xs text-slate-300">Téléphone</div>
                  <div className="text-sm font-semibold">06 32 47 51 74</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-6 p-5 bg-[#FEF3C7] border border-[#D97706]/30 rounded-lg flex items-start gap-3">
          <Info size={18} className="text-[#92400E] shrink-0 mt-0.5" />
          <div className="text-sm text-[#92400E]">
            <span className="font-bold">Attention :</span> le nombre de places est limité. Les inscriptions se font dans l'ordre d'arrivée des dossiers complets.
          </div>
        </div>
      </section>
    </div>
  );
}
